import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogIn } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { authentication, host } from "../../api/ApiCalls";
import { tostifyOption } from "../../utils/toastOptions";
import axios from "axios";
import { toast } from "react-toastify";
import Button from "../../components/ui/Button";
import ForgetPasswordDialog from "./ForgetPasswordDialog";

const Login = () => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [formErrors, setFormErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [signInButtonLoading, setSignInButtonLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const validateForm = () => {
    const errors = {};
    const usernamePattern = /^[A-Za-z]+[A-Za-z0-9_]*$/;

    if (!formData.username.trim()) {
      errors.username = "Username is required.";
    } else if (!usernamePattern.test(formData.username)) {
      errors.username =
        "Username must start with a letter and can include numbers or underscores.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignInButtonLoading(true);
    setFormErrors({});

    if (!validateForm()) {
      toast.error("Please fix the validation errors.", tostifyOption);
      setSignInButtonLoading(false);
      return;
    }

    try {
      const res = await axios.post(`${host}/${authentication}/login`, {
        username: formData.username,
        password: formData.password,
      });

      if (res?.data?.success === false) {
        toast.error(res.data.message, tostifyOption);
        setSignInButtonLoading(false);
        return;
      }

      if (res?.data?.success === true) {
        toast.success(res.data.message, tostifyOption);

        const { setAuth } = useAuthStore.getState(); // Zustand direct call
        setAuth({
          user: res.data.user,
          token: res.data.token,
        });

        localStorage.setItem(
          "academiaHubToken",
          JSON.stringify(res.data.token)
        );
        localStorage.setItem("auth", JSON.stringify(res.data));

        const redirectPath = location.state?.from || "/dashboard";
        navigate(redirectPath, { replace: true });
      }
      setSignInButtonLoading(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Login failed.",
        tostifyOption
      );
    }
    setSignInButtonLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <LogIn className="mx-auto h-12 w-12 text-indigo-600" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                pattern="^[A-Za-z]+[A-Za-z0-9_]*$"
                title="Username must start with a letter and can include numbers or underscores."
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  formErrors.username ? "border-red-500" : "border-gray-300"
                } focus:border-indigo-500 focus:ring-indigo-500`}
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
              />
              {formErrors.username && (
                <p className="text-sm text-red-600 mt-1">
                  {formErrors.username}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  formErrors.password ? "border-red-500" : "border-gray-300"
                } focus:border-indigo-500 focus:ring-indigo-500`}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />

              {formErrors.password && (
                <p className="text-sm text-red-600 mt-1">
                  {formErrors.password}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={signInButtonLoading}
            >
              {signInButtonLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    ></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="mt-6">
            <p className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign up
              </Link>
            </p>
            <div className="text-center mt-3">
              <button
                className="font-medium text-sm text-indigo-600 hover:text-indigo-500"
                onClick={() => setOpen(true)}
              >
                Forgot Password?
              </button>
              <ForgetPasswordDialog
                open={open}
                onClose={() => setOpen(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
